import React, { useEffect, useState } from 'react';
import { HiOutlineSparkles } from 'react-icons/hi2';

interface WidgetComponentProps {
  botId: string;
  onLoad?: () => void;
  mute?: boolean;
  open?: boolean;
  hideMinimizeButton?: boolean;
  hideCloseButton?: boolean;
  loadingText: string;
}

const WidgetComponent: React.FC<WidgetComponentProps> = ({
  botId,
  onLoad,
  mute = false,
  open = false,
  hideMinimizeButton = false,
  hideCloseButton = false,
  loadingText,
}) => {
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const fallbackTimeout = 100;

  useEffect(() => {
    // Set a timeout to force load after specified time
    const timeoutId = setTimeout(() => {
      if (!isIframeLoaded) {
        setLoadingTimeout(true);
        onLoad?.();
      }
    }, fallbackTimeout);

    // Reset states
    setIsIframeLoaded(false);
    setLoadingTimeout(false);

    // Cleanup
    return () => clearTimeout(timeoutId);
  }, [botId, fallbackTimeout, isIframeLoaded, onLoad]);

  const handleIframeLoad = () => {
    setIsIframeLoaded(true);
    onLoad?.();
  };

  return (
    <div className="tw-flex tw-items-center tw-justify-center tw-p-4">
      <div className="tw-w-full tw-max-w-[360px] tw-relative">
        {/* Container with aspect ratio */}
        <div
          className="tw-w-full tw-border tw-border-gray-200 tw-rounded-3xl tw-overflow-hidden tw-relative"
          style={{
            aspectRatio: '360/640',
            minHeight: '400px', // Minimum height for very small screens
            maxHeight: '90vh', // Maximum height to prevent overflow
          }}
        >
          {/* Loading Indicator */}
          {!isIframeLoaded && !loadingTimeout && (
            <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-white/90 tw-z-10">
              <div className="tw-flex tw-flex-col tw-items-center tw-gap-4">
                <div className="tw-animate-spin">
                  <HiOutlineSparkles className="tw-w-8 tw-h-8 tw-text-blue-600" />
                </div>
                <p className="tw-text-gray-600 tw-text-sm tw-text-center tw-px-4">{loadingText}</p>
              </div>
            </div>
          )}

          {/* Iframe */}
          <iframe
            src={`https://widget.louisebot.com/bots/${botId}?pageUrl=https%3A%2F%2Flouisebot.com&open=${open}&hideMinimizeButton=${hideMinimizeButton}&hideCloseButton=${hideCloseButton}&mute=${mute}`}
            allow="microphone"
            title="louisebot.com"
            name="louisebot.com"
            allowFullScreen
            onLoad={handleIframeLoad}
            className="tw-absolute tw-inset-0 tw-w-full tw-h-full"
            style={{
              border: '0px',
              background: 'transparent',
              pointerEvents: 'auto',
              overflow: 'hidden',
              zIndex: 10,
              transition: 'all 0.3s ease',
              borderRadius: '20px',
              opacity: isIframeLoaded || loadingTimeout ? 1 : 0,
              transform: isIframeLoaded || loadingTimeout ? 'scale(1.015)' : 'scale(1)',
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default WidgetComponent;
